import React from 'react';
import { fetchCardsWithCache } from './api/cards';
import { normalizeCard, type Card } from './game/types';
import { useGameStore } from './store/useGameStore';
import { shallow } from 'zustand/shallow';
import Board from './ui/Board';
import Reward from './ui/Reward';
import fateBurnVideoSrc from '../assets/characters/fate_burn.webm';
import buttonLoreImg from '../assets/button_lore.png';
import circleOneImg from '../assets/circle_1.png';
import { getStarterCoinFlipConfig, type StarterCoinFlipKind } from './config/vfx';

// Import combat CSS (scoped by class names; it's okay alongside Tailwind)
import './styles.css';
import type { RelicCombatMods, RelicFeedbackEntry, RelicFeedbackTriggerKey } from './game/types';
import { REWARD_REMOVE_MIN_DECK_SIZE, canRemoveDeckCard } from './game/deckRules';


const starterCoinSheetReadyCache = new Map<string, Promise<void>>();

function isStoryExclusiveAllyCard(card: Card | null | undefined): boolean {
  const subtitle = String((card as any)?.subtitle ?? '').trim().toLowerCase();
  const specialType = String((card as any)?.specialType ?? '').trim().toLowerCase();
  if (specialType === 'ally') return true;
  return /(^|\b)ally\s+(attack|defense|utility)(\b|$)/i.test(subtitle);
}

function ensureStarterCoinSheetReady(sheetUrl: string): Promise<void> {
  if (!sheetUrl) return Promise.resolve();

  const cached = starterCoinSheetReadyCache.get(sheetUrl);
  if (cached) return cached;

  const readyPromise = new Promise<void>((resolve) => {
    const img = new Image();
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const finalizeDecode = () => {
      if (typeof img.decode === 'function') {
        img.decode().catch(() => undefined).finally(finish);
        return;
      }
      finish();
    };

    img.onload = finalizeDecode;
    img.onerror = finish;
    img.src = sheetUrl;

    if (img.complete && img.naturalWidth > 0) {
      finalizeDecode();
    }
  });

  starterCoinSheetReadyCache.set(sheetUrl, readyPromise);
  return readyPromise;
}

type Props = {
  // Dynamic caps from dungeon (base 100 + relic bonuses)
  maxHp?: number;
  maxSp?: number;
  // Dungeon-provided deck card IDs. (If empty, we'll fall back to the demo preset.)
  deckIds: string[];
  // Current dungeon HP/SP (used to start combat with persisted exploration values).
  initialHp?: number;
  initialSp?: number;
  // Called after victory + reward pick (or skip). Should return to dungeon.
  onVictory: (
    rewardCard: Card | null,
    nextDeckIds: string[],
    finalStats?: { hp: number; sp: number; roomLightTier?: string },
    loot?: { teeth: number; keys?: number; potions?: number; torches?: number }
  ) => void;
  // Called on defeat (return to dungeon; you can decide what to do there).
  onDefeat: (finalStats?: { hp: number; sp: number; roomLightTier?: string }) => void;
  // Optional: let the parent pass a seed to keep reward deterministic.
  rewardSeed?: number;

  // Optional: force a specific enemy (e.g. 'cultist' | 'hexbinder')
  enemyId?: string;
  enemyPortraitVariant?: string;

  // Dungeon-provided combat modifiers (typically from trinkets/relics).
  combatMods?: RelicCombatMods;

  /**
   * A list of owned relics (trinkets) from the dungeon crawler. When provided
   * these relics will be forwarded into the combat board so they can be
   * rendered as a relic bar overlay. Each relic should include id, name,
   * description and image fields.
   */
  ownedRelics?: any[];

  /**
   * Number of additional reward choices to present when picking a card after combat.
   * For example, if set to 1 the player will choose from 4 cards instead of 3.
   */
  extraRewardChoices?: number;

  /**
   * If true, the reward picker will offer only a single card instead of three. This
   * flag takes precedence over extraRewardChoices.
   */
  onlyRewardChoice?: boolean;

  /**
   * Exploration lighting values forwarded into combat so the combat scene can
   * mirror the current torch falloff / darkness level with minimal extra logic.
   */
  torchProgress?: number;
  lightRadius?: number;
  lowFx?: boolean;
  aggressiveLowFx?: boolean;
};

type Loot = {
  teeth: number;
  keys?: number;
  potions?: number;
  torches?: number;
};

const MID_BOSS_MUSIC_SRC_BASE = '/audio/mid_boss_fight';
const MONSIGNOR_BOSS_MUSIC_SRC_BASE = '/audio/mid_boss_fight_2';
const PRIME_VESSEL_BOSS_MUSIC_SRC_BASE = '/audio/mid_boss_fight_3';
const BLACKHAND_BOSS_MUSIC_SRC_BASE = '/audio/mid_boss_fight_4';
const SPECIAL_FLOOR_BOSS_MUSIC_SRC_BY_ENEMY_ID: Record<string, string> = {
  nested_horror: MID_BOSS_MUSIC_SRC_BASE,
  monsignor: MONSIGNOR_BOSS_MUSIC_SRC_BASE,
  prime_vessel: PRIME_VESSEL_BOSS_MUSIC_SRC_BASE,
  blackhand: BLACKHAND_BOSS_MUSIC_SRC_BASE,
};
const SPECIAL_FLOOR_BOSS_ENEMY_IDS = new Set(Object.keys(SPECIAL_FLOOR_BOSS_MUSIC_SRC_BY_ENEMY_ID));
const DEFEAT_BURN_START_DELAY_MS = 1000;
const DEFEAT_AUDIO_FADE_OUT_MS = 550;
const DEFEAT_FANFARE_SRC_BASE = '/audio/defeat_fanfare';
const VICTORY_FANFARE_SRC_BASE = '/audio/victory_fanfare';
const DEFEAT_BRAZIER_SFX_SRC = '/audio/sound%20effects/Brazier_Lit.mp3';
const DEFEAT_BURN_PARTICLE_COUNT = 18;


const DEFAULT_COMBAT_THEME_PATTERNS = [
  'combat_theme.mp3',
  'combat_theme1.mp3',
  'combat_theme2.mp3',
  'combat_theme3.mp3',
  'combat_theme4.mp3',
  'combat_theme5.mp3',
  'combat_theme6.mp3',
  'combat_theme7.mp3',
  '/audio/combat_theme',
  '/audio/combat_theme1',
  '/audio/combat_theme2',
  '/audio/combat_theme3',
  '/audio/combat_theme4',
  '/audio/combat_theme5',
  '/audio/combat_theme6',
  '/audio/combat_theme7',
  'combat_theme',
  'combat_theme1',
  'combat_theme2',
  'combat_theme3',
  'combat_theme4',
  'combat_theme5',
  'combat_theme6',
  'combat_theme7',
];

function normalizeAudioSrc(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function isDefaultCombatThemeSrc(value: unknown): boolean {
  const src = normalizeAudioSrc(value);
  if (!src) return false;
  if (Object.values(SPECIAL_FLOOR_BOSS_MUSIC_SRC_BY_ENEMY_ID).some((base) => src.includes(normalizeAudioSrc(base)))) return false;
  return DEFAULT_COMBAT_THEME_PATTERNS.some((pattern) => src.includes(pattern.toLowerCase()));
}

function pickAudioFormatSrc(baseName: string): string {
  if (typeof document === 'undefined') return `${baseName}.mp3`;

  try {
    const test = document.createElement('audio');
    const canPlayOgg = test.canPlayType('audio/ogg; codecs="vorbis"') !== '';
    return canPlayOgg ? `${baseName}.ogg` : `${baseName}.mp3`;
  } catch {
    return `${baseName}.mp3`;
  }
}

function isMidBossMusicSrc(value: unknown): boolean {
  const src = normalizeAudioSrc(value);
  if (!src) return false;
  return Object.values(SPECIAL_FLOOR_BOSS_MUSIC_SRC_BY_ENEMY_ID).some((base) => src.includes(normalizeAudioSrc(base)));
}

function fadeAudioElement(
  audio: HTMLMediaElement,
  targetVolume: number,
  durationMs: number,
  onDone?: () => void
): () => void {
  const safeTarget = Math.max(0, Math.min(1, Number.isFinite(targetVolume) ? targetVolume : 0));
  const startVolume = Math.max(0, Math.min(1, typeof audio.volume === 'number' ? audio.volume : 0));
  const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
  let rafId = 0;
  let finished = false;

  const finish = () => {
    if (finished) return;
    finished = true;
    try {
      audio.volume = safeTarget;
    } catch {}
    onDone?.();
  };

  const tick = (now: number) => {
    const nowMs = typeof now === 'number' ? now : (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const progress = Math.min(1, (nowMs - startTime) / Math.max(1, durationMs));
    const nextVolume = startVolume + (safeTarget - startVolume) * progress;

    try {
      audio.volume = Math.max(0, Math.min(1, nextVolume));
    } catch {}

    if (progress < 1) {
      rafId = window.requestAnimationFrame(tick);
      return;
    }

    finish();
  };

  rafId = window.requestAnimationFrame(tick);

  return () => {
    if (rafId) window.cancelAnimationFrame(rafId);
    finish();
  };
}

// Deterministic loot: always +1 tooth, plus 25% chance for ONE extra resource (key/potion/torch).
function rollLoot(seedInput?: number): Loot {
  let seed = ((seedInput ?? Date.now()) ^ 0xa5a5a5a5) >>> 0;

  const rand = () => {
    // xorshift32
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    return ((seed >>> 0) % 100000) / 100000;
  };

  const loot: Loot = { teeth: 0, keys: 0, potions: 0, torches: 0 };

  // Base teeth: 1–2 (50/50)
  loot.teeth = rand() < 0.5 ? 1 : 2;

  const addRandomResource = () => {
    const pick = Math.floor(rand() * 3); // 0..2
    if (pick === 0) loot.keys = (loot.keys ?? 0) + 1;
    if (pick === 1) loot.potions = (loot.potions ?? 0) + 1;
    if (pick === 2) loot.torches = (loot.torches ?? 0) + 1;
  };

  // 40% chance for one extra random resource
  if (rand() < 0.40) addRandomResource();

  // 25% chance for another random resource (independent)
  if (rand() < 0.25) addRandomResource();

  return loot;
}

function applyBrinkShrineLootBonus(base: Loot, bonus: number): Loot {
  const b = Math.max(0, Number(bonus) || 0);
  if (!base || b <= 0) return base;

  const out: Loot = { ...base };
  out.teeth = (out.teeth ?? 0) + b;

  // Only boost resources that actually dropped (we don't create new ones out of thin air).
  if ((out.keys ?? 0) > 0) out.keys = (out.keys ?? 0) + b;
  if ((out.potions ?? 0) > 0) out.potions = (out.potions ?? 0) + b;
  if ((out.torches ?? 0) > 0) out.torches = (out.torches ?? 0) + b;

  return out;
}

function uniq<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}


function appendTrigger(
  map: Partial<Record<RelicFeedbackTriggerKey, string | string[]>>,
  trigger: RelicFeedbackTriggerKey,
  relicId: string
) {
  const cur = map[trigger];
  if (!cur) {
    map[trigger] = relicId;
    return;
  }
  if (Array.isArray(cur)) {
    if (!cur.includes(relicId)) cur.push(relicId);
    return;
  }
  if (cur !== relicId) map[trigger] = [cur, relicId];
}

function appendFeedbackEntry(
  map: Partial<Record<RelicFeedbackTriggerKey, RelicFeedbackEntry[]>>,
  trigger: RelicFeedbackTriggerKey,
  entry: RelicFeedbackEntry
) {
  const list = map[trigger] ? [...(map[trigger] as RelicFeedbackEntry[])] : [];
  // Keep each feedback contribution separate.
  //
  // We previously merged entries that shared the same relic id + text, which could
  // collapse distinct effects into a single summed badge (for example showing
  // +30 Armor on Iron Bracers when two separate armor contributions were firing).
  //
  // By preserving each contribution as its own entry, the engine can distribute the
  // real triggered amount across them without overstating a single relic's feedback.
  list.push(entry);
  map[trigger] = list;
}

function buildFeedbackRelicMap(
  relics: any[] | undefined,
  mods: RelicCombatMods | undefined
): {
  feedbackRelicIdByTrigger: Partial<Record<RelicFeedbackTriggerKey, string | string[]>>;
  feedbackRelicEntriesByTrigger: Partial<Record<RelicFeedbackTriggerKey, RelicFeedbackEntry[]>>;
} {
  const feedbackRelicIdByTrigger: Partial<Record<RelicFeedbackTriggerKey, string | string[]>> = {};
  const feedbackRelicEntriesByTrigger: Partial<Record<RelicFeedbackTriggerKey, RelicFeedbackEntry[]>> = {};
  const list = Array.isArray(relics) ? relics : [];

  const addAmount = (trigger: RelicFeedbackTriggerKey, relicId: string, value: unknown) => {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount === 0) return;
    appendTrigger(feedbackRelicIdByTrigger, trigger, relicId);
    appendFeedbackEntry(feedbackRelicEntriesByTrigger, trigger, { relicId, amount });
  };

  const addText = (trigger: RelicFeedbackTriggerKey, relicId: string, text?: string) => {
    if (!text) return;
    appendTrigger(feedbackRelicIdByTrigger, trigger, relicId);
    appendFeedbackEntry(feedbackRelicEntriesByTrigger, trigger, { relicId, text });
  };

  const addTriggerOnly = (trigger: RelicFeedbackTriggerKey, relicId: string) => {
    appendTrigger(feedbackRelicIdByTrigger, trigger, relicId);
    appendFeedbackEntry(feedbackRelicEntriesByTrigger, trigger, { relicId });
  };

  const hasEnabled = (value: unknown) => {
    if (typeof value === 'boolean') return value;
    return Number(value) > 0;
  };

  for (const relic of list) {
    const relicId = String(relic?.id ?? '').trim();
    if (!relicId) continue;
    const combat = relic?.combat ?? {};

    addAmount('startArmor', relicId, combat.startArmor);
    addAmount('startHp', relicId, combat.startHp);
    addAmount('startSp', relicId, combat.startSp);
    addAmount('startReflectPct', relicId, combat.startReflectPct);
    addAmount('startNegateNextDamage', relicId, combat.startNegateNextDamage);
    addAmount('turnStartAttackBonus', relicId, combat.turnStartAttackBonus);
    addAmount('turnStartArmor', relicId, combat.turnStartArmor);
    addAmount('firstTurnExtraDraw', relicId, combat.firstTurnExtraDraw);
    addAmount('drawBeyondFirstGainSp', relicId, combat.drawBeyondFirstGainSp);
    addAmount('fullHandGainHpOnDraw', relicId, combat.fullHandGainHpOnDraw);
    addAmount('onDiscardGainSp', relicId, combat.onDiscardGainSp);
    addAmount('onDiscardGainHp', relicId, combat.onDiscardGainHp);
    addAmount('delayCardArmor', relicId, combat.delayCardArmor);
    addAmount('spGainWhenZero', relicId, combat.spGainWhenZero);
    addAmount('reflectBonusHp', relicId, combat.reflectBonusHp);
    addAmount('reflectBonusSp', relicId, combat.reflectBonusSp);
    addAmount('reflectBonusDraw', relicId, combat.reflectBonusDraw);
    addAmount('attackBonusFlat', relicId, combat.attackBonusFlat);
    addAmount('attackBonusWhenLowSp', relicId, combat.attackBonusWhenLowSp);
    addAmount('attackBonusWhenArmorZero', relicId, combat.attackBonusWhenArmorZero);
    addAmount('endTurnEmptyDraw', relicId, combat.endTurnEmptyDraw);
    addAmount('endTurnFiveCardsArmor', relicId, combat.endTurnFiveCardsArmor);
    addAmount('endTurnArmorPerCard', relicId, combat.endTurnArmorPerCard);
    addAmount('chainCostReduction', relicId, combat.chainCostReduction);
    addAmount('delayCostReduction', relicId, combat.delayCostReduction);
    addAmount('addendumCostReduction', relicId, combat.addendumCostReduction);
    addAmount('focusBonusSP', relicId, combat.focusBonusSP);
    addAmount('focusBonusHP', relicId, combat.focusBonusHP);

    if (mods?.damageReductionIfLargeDeck && combat.damageReductionIfLargeDeck) {
      addAmount('damageReductionIfLargeDeck', relicId, combat.damageReductionIfLargeDeck);
    }
    if (mods?.reduceDelayForLargeDeck && combat.reduceDelayForLargeDeck) {
      addAmount('reduceDelayForLargeDeck', relicId, combat.reduceDelayForLargeDeck);
    }
    if (combat.doubleArmorGainFromCards === true) {
      addTriggerOnly('doubleArmorGainFromCards', relicId);
    }

    if (hasEnabled(combat.startNextCardCost0)) {
      addText('startNextCardCost0', relicId, 'Next card costs 0');
    }
    if (hasEnabled(combat.firstExileDontExile)) {
      addText('firstExileDontExile', relicId, 'Exile prevented');
    }
    if (combat.immuneStun === true) {
      addText('immuneStun', relicId, 'Stun negated');
    }
    if (typeof combat.recastDrawPenalty === 'number' && combat.recastDrawPenalty <= 0) {
      addText('recastDrawPenalty', relicId, 'Recast penalty removed');
    }
  }

  return { feedbackRelicIdByTrigger, feedbackRelicEntriesByTrigger };
}

const ZERO_REWARD_ENEMY_IDS = new Set([
  'prime_crucible_device',
  'pulse_engine_device',
  'synaptic_retort_device',
  'astral_reliquary_device',
]);

export default function CombatOverlay({ deckIds, initialHp, initialSp, maxHp, maxSp, onVictory, onDefeat, rewardSeed, enemyId, enemyPortraitVariant, combatMods, ownedRelics, extraRewardChoices = 0, onlyRewardChoice = false, torchProgress = 1, lightRadius, lowFx = false, aggressiveLowFx = false }: Props) {
  const forcedEnemyId = typeof enemyId === 'string' ? enemyId : '';
  const suppressStandardRewards = forcedEnemyId.startsWith('yahaka_voidfeeder') || ZERO_REWARD_ENEMY_IDS.has(forcedEnemyId);
  const {
    state,
    dispatch,
    loadCards,
    library,
    selected,
    applyProvidedPreset,
    startCombat,
    backToSetup,
    setDeckFromIds,
    addCardToDeckNoRestart,
    setRelicMods,
  } = useGameStore(
    (s) => ({
      state: s.state,
      dispatch: s.dispatch,
      loadCards: s.loadCards,
      library: s.library,
      selected: s.selected,
      applyProvidedPreset: s.applyProvidedPreset,
      startCombat: s.startCombat,
      backToSetup: s.backToSetup,
      setDeckFromIds: s.setDeckFromIds,
      addCardToDeckNoRestart: s.addCardToDeckNoRestart,
      setRelicMods: s.setRelicMods,
    }),
    shallow
  );

  const [booted, setBooted] = React.useState(false);
  const [starterCoinState, setStarterCoinState] = React.useState<'hidden' | 'preloading' | 'playing' | 'done'>('hidden');
  const [starterCoinOutcome, setStarterCoinOutcome] = React.useState<StarterCoinFlipKind>('cara');
  const [defeatCardStage, setDefeatCardStage] = React.useState<'idle' | 'burning' | 'done'>('idle');
  const [defeatBurnPlaybackKey, setDefeatBurnPlaybackKey] = React.useState(0);
  const startedRef = React.useRef(false);
  const defeatVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const pendingDefeatVideoStartRef = React.useRef(false);
  const bossMusicRef = React.useRef<HTMLAudioElement | null>(null);
  const defeatFanfareRef = React.useRef<HTMLAudioElement | null>(null);
  const victoryFanfareRef = React.useRef<HTMLAudioElement | null>(null);
  const rewardFanfarePlayedRef = React.useRef(false);
  const defeatBurnSfxRef = React.useRef<HTMLAudioElement | null>(null);
  const starterCoinTimerRef = React.useRef<number | null>(null);
  const starterCoinConsumedRef = React.useRef(false);
  const starterCoinLaunchTokenRef = React.useRef(0);
  const starterCoinViewportRef = React.useRef<HTMLDivElement | null>(null);
  const starterCoinSheetRef = React.useRef<HTMLDivElement | null>(null);

  const clearStarterCoinTimer = React.useCallback(() => {
    if (starterCoinTimerRef.current != null) {
      window.clearTimeout(starterCoinTimerRef.current);
      starterCoinTimerRef.current = null;
    }
  }, []);

  const starterCoinConfig = React.useMemo(() => getStarterCoinFlipConfig(starterCoinOutcome), [starterCoinOutcome]);
  const starterCoinAnimationDurationMs = React.useMemo(
    () => Math.round((starterCoinConfig.frameCount / Math.max(1, starterCoinConfig.fps)) * 1000),
    [starterCoinConfig]
  );

  React.useEffect(() => {
    void ensureStarterCoinSheetReady(getStarterCoinFlipConfig('cara').sheetUrl);
    void ensureStarterCoinSheetReady(getStarterCoinFlipConfig('cruz').sheetUrl);
  }, []);

  const tryStartDefeatVideo = React.useCallback(() => {
    if (!pendingDefeatVideoStartRef.current) return;

    const video = defeatVideoRef.current;
    if (!video) return;

    setDefeatCardStage('burning');

    let burnSfxAudio = defeatBurnSfxRef.current;
    if (!burnSfxAudio) {
      burnSfxAudio = new Audio(DEFEAT_BRAZIER_SFX_SRC);
      burnSfxAudio.preload = 'auto';
      burnSfxAudio.loop = false;
      defeatBurnSfxRef.current = burnSfxAudio;
    }

    try {
      burnSfxAudio.pause();
      burnSfxAudio.currentTime = 0;
    } catch {}

    burnSfxAudio.loop = false;
    burnSfxAudio.muted = false;
    burnSfxAudio.volume = 1;

    const sfxPlayPromise = burnSfxAudio.play();
    if (sfxPlayPromise && typeof (sfxPlayPromise as Promise<void>).catch === 'function') {
      (sfxPlayPromise as Promise<void>).catch(() => undefined);
    }

    try {
      video.pause();
      video.currentTime = 0;
    } catch {}

    const playPromise = video.play();
    if (playPromise && typeof (playPromise as Promise<void>).catch === 'function') {
      (playPromise as Promise<void>).catch(() => undefined);
    }
  }, []);

  const defeatBurnParticles = React.useMemo(
    () => Array.from({ length: DEFEAT_BURN_PARTICLE_COUNT }, (_, index) => {
      const spread = ((index * 37) % 100) / 100;
      const scatter = ((index * 19 + 11) % 100) / 100;
      const lift = ((index * 29 + 7) % 100) / 100;
      const delay = ((index * 13) % 9) * 55;
      const duration = 820 + ((index * 71) % 520);
      const size = 4 + ((index * 3) % 9);
      const startX = 18 + spread * 48;
      const startY = 28 + scatter * 54;
      const driftX = 20 + scatter * 44;
      const driftY = -(40 + lift * 92);

      return {
        id: `burn-particle-${index}`,
        style: {
          '--particle-start-x': `${startX}%`,
          '--particle-start-y': `${startY}%`,
          '--particle-drift-x': `${driftX}px`,
          '--particle-drift-y': `${driftY}px`,
          '--particle-size': `${size}px`,
          '--particle-duration': `${duration}ms`,
          '--particle-delay': `${delay}ms`,
          '--particle-opacity': `${(0.42 + spread * 0.34).toFixed(2)}`,
        } as React.CSSProperties,
      };
    }),
    [defeatBurnPlaybackKey]
  );
  const originalMediaPlayRef = React.useRef<typeof HTMLMediaElement.prototype.play | null>(null);
  const suppressedCombatAudioRef = React.useRef<
    Array<{
      el: HTMLMediaElement;
      previousMuted: boolean;
      previousVolume: number;
    }>
  >([]);

  // Precompute deterministic loot preview (shown in the reward modal + applied on exit).
  const lootPreview = React.useMemo(
    () => (suppressStandardRewards ? { teeth: 0, keys: 0, potions: 0, torches: 0 } : rollLoot(rewardSeed)),
    [rewardSeed, suppressStandardRewards]
  );

  // If the Brink Shrine relic is owned and the player ends combat at low SP,
  // increase the quantity of the loot from this combat (preview shown in the reward modal).
  const lootPreviewForReward = React.useMemo(() => {
    if (state.phase !== 'reward') return lootPreview;

    const mods: any = state.relicMods;
    const bonus = Math.max(0, Number(mods?.brinkShrineLootQuantityBonus ?? 0) || 0);
    const spAtEnd = Number((state as any).endCombatSp ?? state.player?.sp ?? 0) || 0;

    // Condition: SP is 20 or less at the end of combat.
    if (bonus > 0 && spAtEnd <= 20) {
      return applyBrinkShrineLootBonus(lootPreview, bonus);
    }
    return lootPreview;
  }, [lootPreview, state.phase, (state as any).endCombatSp, state.player?.sp, state.relicMods]);

  // Apply Teeth bonuses that depend on combat outcome flags (shown in reward modal and returned to dungeon).
  // Note: Brink Shrine quantity bonuses are applied first via `lootPreviewForReward`.
  const lootFinalForReward = React.useMemo(() => {
    let teethBonus = 0;
    const mods: any = state.relicMods;

    // Untarnished Candle: if you took no HP damage, gain Teeth (HP penalty is handled in-engine).
    if (
      mods &&
      mods.untarnishedCandleLoseHp &&
      !(state.playerTookEnemyDamageThisCombat || state.playerTookDamage) &&
      typeof mods.untarnishedCandleTeeth !== 'undefined'
    ) {
      const gain = Number(mods.untarnishedCandleTeeth) || 0;
      if (gain > 0) teethBonus += gain;
    }

    // Zero Fang (Ivory Tooth): if the enemy was defeated by exact lethal, gain Teeth.
    if (mods && typeof mods.ivoryTooth !== 'undefined' && state.enemyKilledExactlyAtZero) {
      const gain = Number(mods.ivoryTooth) || 0;
      if (gain > 0) teethBonus += gain;
    }

    const base = lootPreviewForReward;
    return {
      ...base,
      teeth: (base.teeth || 0) + teethBonus,
    } as Loot;
  }, [lootPreviewForReward, state.playerTookDamage, state.playerTookEnemyDamageThisCombat, state.enemyKilledExactlyAtZero, state.relicMods]);


  // Load cards once (combat store is global via zustand).
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      if (state.phase !== 'loading') {
        setBooted(true);
        return;
      }
      dispatch({ type: 'LOAD_START' });
      try {
        const raw = await fetchCardsWithCache();
        const cards = raw.map(normalizeCard);
        if (!mounted) return;
        loadCards(cards);
        setBooted(true);
      } catch (e: any) {
        if (!mounted) return;
        dispatch({ type: 'LOAD_ERROR', error: e?.message ?? 'Failed to load cards' });
        setBooted(true);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line
  }, []);

  // Ensure a sane deck selection, then start combat exactly once per mount.
  React.useEffect(() => {
    if (!booted) return;
    if (state.phase !== 'setup') return;
    if (startedRef.current) return;

    // Apply dungeon-provided relic mods before starting combat.
// (If none are provided, reset to defaults so mods don't leak between fights.)
// Merge dungeon combat mods with persisted exploration HP/SP so combat starts from current values.

// Combat base is 100/100; startHp/startSp are treated as offsets (can be negative).
// Guard against accidental 0/NaN values (0 would become -100 and instantly kill the player).
const hasHp =
  typeof initialHp === 'number' &&
  Number.isFinite(initialHp) &&
  initialHp > 0;

const hasSp =
  typeof initialSp === 'number' &&
  Number.isFinite(initialSp);
// Preserve a valid 0 SP entry value when combat starts.
// The offset math below already clamps invalid negatives up to 0.

const capHp =
  typeof maxHp === 'number' && Number.isFinite(maxHp) && maxHp > 0 ? Math.floor(maxHp) : 100;
const capSp =
  typeof maxSp === 'number' && Number.isFinite(maxSp) && maxSp > 0 ? Math.floor(maxSp) : 100;

const hpOffset = hasHp ? (Math.max(0, Math.min(capHp, Math.floor(initialHp))) - 100) : 0;
const spOffset = hasSp ? (Math.max(0, Math.min(capSp, Math.floor(initialSp))) - 100) : 0;

const relicStartHp = Math.floor(Number((combatMods as any)?.startHp ?? 0) || 0);
const relicStartSp = Math.floor(Number((combatMods as any)?.startSp ?? 0) || 0);
const relicTurnStartAttackBonus = Math.floor(Number((combatMods as any)?.turnStartAttackBonus ?? 0) || 0);

const mergedMods = {
  ...(combatMods ?? {}),
  // Normalize legacy relic payloads that still use `attackBonus` for the
  // first-Attack-each-turn modifier (e.g. Hidden Dagger).
  turnStartAttackBonus: relicTurnStartAttackBonus,
  // NEW: pass caps into combat so engine can clamp to them
  capHp,
  capSp,
  feedbackStartHp: relicStartHp,
  feedbackStartSp: relicStartSp,
  startHp: relicStartHp + hpOffset,
  startSp: relicStartSp + spOffset,
  lampKeeperInitialTorchProgress: torchProgress,
} as any;

const { feedbackRelicIdByTrigger, feedbackRelicEntriesByTrigger } = buildFeedbackRelicMap(ownedRelics, mergedMods);
if (Object.keys(feedbackRelicIdByTrigger).length > 0) {
  mergedMods.feedbackRelicIdByTrigger = feedbackRelicIdByTrigger;
}
if (Object.keys(feedbackRelicEntriesByTrigger).length > 0) {
  mergedMods.feedbackRelicEntriesByTrigger = feedbackRelicEntriesByTrigger;
}

setRelicMods(mergedMods);



    // If dungeon sent a deck, use it. Otherwise, apply the built-in preset.
    if (deckIds && deckIds.length > 0) {
      setDeckFromIds(deckIds);
    } else {
      applyProvidedPreset();
    }

    startedRef.current = true;
    startCombat(enemyId);
  }, [applyProvidedPreset, booted, combatMods, deckIds, enemyId, ownedRelics, setDeckFromIds, setRelicMods, startCombat, state.phase]);


  const activeEnemyId = enemyId ?? state.enemyId;
  const normalizedActiveEnemyId = String(activeEnemyId ?? '').trim().toLowerCase();
  const isFloor4BossEncounter = SPECIAL_FLOOR_BOSS_ENEMY_IDS.has(normalizedActiveEnemyId);
  const bossMusicSrc = pickAudioFormatSrc(
    SPECIAL_FLOOR_BOSS_MUSIC_SRC_BY_ENEMY_ID[normalizedActiveEnemyId] || MID_BOSS_MUSIC_SRC_BASE,
  );

  React.useEffect(() => {
    const sheetEl = starterCoinSheetRef.current;
    const viewportEl = starterCoinViewportRef.current;

    if (!sheetEl || !viewportEl) return;

    const config = getStarterCoinFlipConfig(starterCoinOutcome);

    const renderFrame = (frame: number) => {
      const safeFrame = Math.max(0, Math.min(config.frameCount - 1, frame));
      const col = safeFrame % config.columns;
      const row = Math.floor(safeFrame / config.columns);
      const viewportWidth = Math.max(1, viewportEl.clientWidth);
      const viewportHeight = Math.max(1, viewportEl.clientHeight);

      sheetEl.style.backgroundSize = `${viewportWidth * config.columns}px ${viewportHeight * config.rows}px`;
      sheetEl.style.backgroundPosition = `${-col * viewportWidth}px ${-row * viewportHeight}px`;
    };

    renderFrame(0);

    if (starterCoinState !== 'playing') return;

    const frameDurationMs = 1000 / Math.max(1, config.fps);
    const durationMs = Math.max(1, Math.round((config.frameCount / Math.max(1, config.fps)) * 1000));
    let rafId = 0;
    let lastFrame = -1;
    const startAt = performance.now();

    const tick = (now: number) => {
      const elapsed = Math.min(durationMs, now - startAt);
      const nextFrame = Math.min(config.frameCount - 1, Math.floor(elapsed / frameDurationMs));

      if (nextFrame !== lastFrame) {
        lastFrame = nextFrame;
        renderFrame(nextFrame);
      }

      if (elapsed < durationMs) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        renderFrame(config.frameCount - 1);
      }
    };

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        renderFrame(Math.max(0, lastFrame));
      });
      resizeObserver.observe(viewportEl);
    }

    rafId = window.requestAnimationFrame(tick);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
    };
  }, [starterCoinOutcome, starterCoinState]);

  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    (window as any).__dd_combatEnemyId = activeEnemyId ?? null;

    return () => {
      if ((window as any).__dd_combatEnemyId === activeEnemyId) {
        (window as any).__dd_combatEnemyId = null;
      }
    };
  }, [activeEnemyId]);
  const shouldSuppressDefaultCombatMusic =
    isFloor4BossEncounter &&
    state.phase !== 'reward' &&
    state.phase !== 'gameover';

  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const restoreSuppressedCombatAudio = () => {
      for (const entry of suppressedCombatAudioRef.current) {
        if (!entry?.el) continue;
        entry.el.muted = entry.previousMuted;
        entry.el.volume = entry.previousVolume;
      }
      suppressedCombatAudioRef.current = [];
    };

    // Helper to track an audio element that has been suppressed. Avoids duplicate tracking.
    const rememberSuppressedCombatAudio = (el: HTMLMediaElement) => {
      const alreadyTracked = suppressedCombatAudioRef.current.some(
        (entry) => entry.el === el,
      );
      if (alreadyTracked) return;

      suppressedCombatAudioRef.current.push({
        el,
        previousMuted: el.muted,
        previousVolume: el.volume,
      });
    };

    // Suppress a single audio element if it matches the default combat theme.
    const suppressCombatAudioElement = (
      el: HTMLMediaElement | null | undefined,
    ) => {
      if (!(el instanceof HTMLMediaElement)) return;

      const src = el.currentSrc || el.src || '';
      if (!isDefaultCombatThemeSrc(src)) return;

      rememberSuppressedCombatAudio(el);
      el.muted = true;
      el.volume = 0;
      el.pause();
    };

    // Scan a root element (or document) and suppress any matching audio elements.
    const suppressMatchingCombatAudioInRoot = (
      root: Document | Element | DocumentFragment | null,
    ) => {
      if (!root) return;

      const mediaEls = Array.from(
        root.querySelectorAll('audio'),
      ) as HTMLMediaElement[];
      for (const el of mediaEls) {
        suppressCombatAudioElement(el);
      }
    };

    // When a new node or attribute change occurs, determine if there is an audio element
    // that needs to be suppressed, either the node itself or within its subtree.
    const suppressMatchingCombatAudioFromNode = (node: Node | null) => {
      if (!node) return;

      if (node instanceof HTMLMediaElement) {
        suppressCombatAudioElement(node);
        return;
      }

      if (!(node instanceof Element)) return;

      const closestAudio = node.closest('audio');
      if (closestAudio instanceof HTMLMediaElement) {
        suppressCombatAudioElement(closestAudio);
      }

      suppressMatchingCombatAudioInRoot(node);
    };

    if (!shouldSuppressDefaultCombatMusic) {
      restoreSuppressedCombatAudio();
      if (
        originalMediaPlayRef.current &&
        window.HTMLMediaElement?.prototype
      ) {
        window.HTMLMediaElement.prototype.play =
          originalMediaPlayRef.current;
        originalMediaPlayRef.current = null;
      }
      return;
    }

    const proto = window.HTMLMediaElement?.prototype;
    if (!proto) return;

    // Patch the play function only once to intercept default combat theme playback.
    if (!originalMediaPlayRef.current) {
      const originalPlay = proto.play;
      originalMediaPlayRef.current = originalPlay;

      proto.play = function patchedCombatThemePlay(
        this: HTMLMediaElement,
        ...args: any[]
      ) {
        const src = this.currentSrc || this.src || '';
        if (isDefaultCombatThemeSrc(src)) {
          rememberSuppressedCombatAudio(this);
          this.muted = true;
          this.volume = 0;
          this.pause();
          return Promise.resolve(undefined);
        }
        return originalPlay.apply(this, args as any);
      } as typeof HTMLMediaElement.prototype.play;
    }

    // Immediately suppress any default combat audio that might already exist.
    suppressMatchingCombatAudioInRoot(document);

    const mutationRoot = document.body || document.documentElement;
    let observer: MutationObserver | null = null;

    // Observe the DOM for new audio elements or src attribute changes.
    if (mutationRoot && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              suppressMatchingCombatAudioFromNode(node);
            });
            continue;
          }

          if (mutation.type === 'attributes') {
            suppressMatchingCombatAudioFromNode(mutation.target);
          }
        }
      });

      observer.observe(mutationRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src'],
      });
    }

    return () => {
      // Disconnect the mutation observer to avoid memory leaks.
      observer?.disconnect();
      restoreSuppressedCombatAudio();
      if (
        originalMediaPlayRef.current &&
        window.HTMLMediaElement?.prototype
      ) {
        window.HTMLMediaElement.prototype.play =
          originalMediaPlayRef.current;
        originalMediaPlayRef.current = null;
      }
    };
  }, [shouldSuppressDefaultCombatMusic]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const shouldPlayBossTrack =
      isFloor4BossEncounter &&
      (state.phase === 'player' || state.phase === 'enemy');

    if (!bossMusicRef.current) {
      const audio = new Audio(bossMusicSrc);
      audio.loop = true;
      audio.preload = 'auto';
      bossMusicRef.current = audio;
    }

    const bossAudio = bossMusicRef.current;
    if (!bossAudio) return;

    const nextBossSrc = new URL(bossMusicSrc, window.location.href).href;
    if (!bossAudio.src || bossAudio.src !== nextBossSrc) {
      bossAudio.pause();
      bossAudio.src = bossMusicSrc;
      try {
        bossAudio.currentTime = 0;
      } catch {}
    }

    if (shouldPlayBossTrack) {
      bossAudio.loop = true;
      bossAudio.muted = false;
      bossAudio.volume = 1;
      const playPromise = bossAudio.play();
      if (playPromise && typeof (playPromise as Promise<void>).catch === 'function') {
        (playPromise as Promise<void>).catch(() => undefined);
      }
      return;
    }

    if (state.phase === 'gameover') return;

    bossAudio.pause();
    try {
      bossAudio.currentTime = 0;
    } catch {}
  }, [bossMusicSrc, isFloor4BossEncounter, state.phase]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;

    clearStarterCoinTimer();

    if (state.phase === 'loading' || state.phase === 'setup') {
      starterCoinLaunchTokenRef.current += 1;
      starterCoinConsumedRef.current = false;
      setStarterCoinState('hidden');
      return () => {
        cancelled = true;
      };
    }

    if (state.phase === 'reward' || state.phase === 'gameover') {
      starterCoinLaunchTokenRef.current += 1;
      setStarterCoinState('done');
      return () => {
        cancelled = true;
      };
    }

    if ((state.phase === 'player' || state.phase === 'enemy') && !starterCoinConsumedRef.current) {
      const launchToken = starterCoinLaunchTokenRef.current + 1;
      const nextOutcome: StarterCoinFlipKind = state.phase === 'player' ? 'cara' : 'cruz';
      const nextConfig = getStarterCoinFlipConfig(nextOutcome);

      starterCoinLaunchTokenRef.current = launchToken;
      setStarterCoinOutcome(nextOutcome);
      setStarterCoinState('preloading');

      void ensureStarterCoinSheetReady(nextConfig.sheetUrl).then(() => {
        if (cancelled) return;
        if (starterCoinLaunchTokenRef.current !== launchToken) return;

        starterCoinConsumedRef.current = true;
        setStarterCoinOutcome(nextOutcome);
        setStarterCoinState('playing');
      });
    }

    return () => {
      cancelled = true;
    };
  }, [clearStarterCoinTimer, state.phase]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    clearStarterCoinTimer();

    if (starterCoinState !== 'playing') return;

    const launchToken = starterCoinLaunchTokenRef.current;
    let cancelled = false;
    let rafA = 0;
    let rafB = 0;

    rafA = window.requestAnimationFrame(() => {
      rafB = window.requestAnimationFrame(() => {
        if (cancelled) return;
        if (starterCoinLaunchTokenRef.current !== launchToken) return;

        starterCoinTimerRef.current = window.setTimeout(() => {
          if (cancelled) return;
          if (starterCoinLaunchTokenRef.current !== launchToken) return;
          starterCoinTimerRef.current = null;
          setStarterCoinState('done');
        }, starterCoinAnimationDurationMs);
      });
    });

    return () => {
      cancelled = true;
      if (rafA) window.cancelAnimationFrame(rafA);
      if (rafB) window.cancelAnimationFrame(rafB);
      clearStarterCoinTimer();
    };
  }, [clearStarterCoinTimer, starterCoinAnimationDurationMs, starterCoinState]);

  React.useEffect(() => {
    return () => {
      starterCoinLaunchTokenRef.current += 1;
      clearStarterCoinTimer();
    };
  }, [clearStarterCoinTimer]);

  const starterCoinShouldLaunchNow =
    !starterCoinConsumedRef.current &&
    (state.phase === 'player' || state.phase === 'enemy');

  const suppressInitialTurnHandoff = starterCoinState === 'preloading' || starterCoinState === 'playing' || starterCoinShouldLaunchNow;
  const shouldHoldCombatBoardForStarterCoin = false;

  React.useEffect(() => {
    return () => {
      const bossAudio = bossMusicRef.current;
      if (bossAudio) {
        bossAudio.pause();
        try {
          bossAudio.currentTime = 0;
        } catch {}
      }

      const defeatFanfare = defeatFanfareRef.current;
      if (defeatFanfare) {
        defeatFanfare.pause();
        try {
          defeatFanfare.currentTime = 0;
        } catch {}
      }

      const victoryFanfare = victoryFanfareRef.current;
      if (victoryFanfare) {
        victoryFanfare.pause();
        try {
          victoryFanfare.currentTime = 0;
        } catch {}
      }

      const defeatBurnSfx = defeatBurnSfxRef.current;
      if (defeatBurnSfx) {
        defeatBurnSfx.pause();
        try {
          defeatBurnSfx.currentTime = 0;
        } catch {}
      }

      if (typeof window !== 'undefined' && originalMediaPlayRef.current && window.HTMLMediaElement?.prototype) {
        window.HTMLMediaElement.prototype.play = originalMediaPlayRef.current;
        originalMediaPlayRef.current = null;
      }

      for (const entry of suppressedCombatAudioRef.current) {
        if (!entry?.el) continue;
        entry.el.muted = entry.previousMuted;
        entry.el.volume = entry.previousVolume;
      }
      suppressedCombatAudioRef.current = [];
      bossMusicRef.current = null;
      defeatFanfareRef.current = null;
      victoryFanfareRef.current = null;
      rewardFanfarePlayedRef.current = false;
      defeatBurnSfxRef.current = null;
    };
  }, []);

  // Roll reward choices when we enter reward.
  React.useEffect(() => {
    if (state.phase !== 'reward') return;
    if (suppressStandardRewards) return;
    if (state.rewardChoices.length > 0) return;

    const inDeck = new Set(state.pool.map((c) => c.id));
    const eligible = library.filter((c) => c.kind !== 'enemy' && !c.acquisitionLocked && !isStoryExclusiveAllyCard(c) && !inDeck.has(c.id));

    // Deterministic shuffle if seed is provided.
    const shuffled = [...eligible];
    let seed = (rewardSeed ?? Date.now()) >>> 0;
    const rand = () => {
      // xorshift32
      seed ^= seed << 13;
      seed ^= seed >>> 17;
      seed ^= seed << 5;
      return ((seed >>> 0) % 100000) / 100000;
    };

    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // Determine how many reward choices to present. When `onlyRewardChoice` is true
    // we force a single choice regardless of any bonus. Otherwise we offer the
    // base 3 plus any extra from tarot effects.
    const rewardCount = onlyRewardChoice ? 1 : 3 + (extraRewardChoices ?? 0);
    const choices = shuffled.slice(0, rewardCount);
    dispatch({ type: 'SET_REWARD_CHOICES', choices });
  }, [dispatch, library, rewardSeed, state.phase, state.pool, state.rewardChoices.length, extraRewardChoices, onlyRewardChoice, suppressStandardRewards]);

  const exitToDungeonAfterReward = (picked: Card | null, forcedNextIds?: string[]) => {
  if (picked) {
    // store-side safety (also prevents duplicates since rewards exclude in-deck cards)
    addCardToDeckNoRestart(picked.id);
  }

  // IMPORTANT: in dungeon mode, trust the combat deck (state.pool), not the store 'selected'.
  const baseIds = forcedNextIds ?? state.pool.map((c) => c.id);

  // Add picked (if any), keep unique
  const nextIds = uniq([...baseIds, ...(picked ? [picked.id] : [])]);

    // Capture final HP/SP before resetting combat state
    const finalStats = { hp: state.player.hp, sp: state.player.sp, roomLightTier: String((state as any).lampKeeperRoomLight ?? '') || undefined };
    // Use the same loot that is shown in the reward modal.
    // This includes Brink Shrine quantity bonuses (based on end-of-combat SP snapshot) and Teeth bonuses from relics.
    const finalLoot = suppressStandardRewards
      ? undefined
      : (lootFinalForReward as { teeth: number; keys?: number; potions?: number; torches?: number });

    backToSetup();
    onVictory(picked, nextIds, finalStats, finalLoot);
    console.log('NEXT DECK SIZE', nextIds.length, nextIds);

};

  React.useEffect(() => {
    if (state.phase !== 'reward') return;
    if (!suppressStandardRewards) return;
    exitToDungeonAfterReward(null);
  }, [state.phase, suppressStandardRewards]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    if (state.phase !== 'reward' || suppressStandardRewards || state.rewardChoices.length <= 0) {
      rewardFanfarePlayedRef.current = false;
      const victoryAudio = victoryFanfareRef.current;
      if (victoryAudio) {
        victoryAudio.pause();
        try {
          victoryAudio.currentTime = 0;
        } catch {}
      }
      return;
    }

    if (rewardFanfarePlayedRef.current) return;
    rewardFanfarePlayedRef.current = true;

    const activeCombatAudio = new Set<HTMLMediaElement>();
    const singletonCombatAudio = (window as any).__dd_combatMusic;
    if (singletonCombatAudio instanceof HTMLMediaElement) {
      activeCombatAudio.add(singletonCombatAudio);
    }
    if (bossMusicRef.current instanceof HTMLMediaElement) {
      activeCombatAudio.add(bossMusicRef.current);
    }

    const mediaEls = Array.from(document.querySelectorAll('audio')) as HTMLMediaElement[];
    for (const el of mediaEls) {
      const src = el.currentSrc || el.src || '';
      if (isDefaultCombatThemeSrc(src) || isMidBossMusicSrc(src)) {
        activeCombatAudio.add(el);
      }
    }

    const fadeCancels: Array<() => void> = [];
    const stopCombatAudio = (audio: HTMLMediaElement) => {
      try {
        audio.pause();
      } catch {}
      try {
        audio.currentTime = 0;
      } catch {}
      try {
        audio.volume = 0;
      } catch {}
      if ((window as any).__dd_combatMusic === audio) {
        try {
          audio.src = '';
        } catch {}
        (window as any).__dd_combatMusic = null;
      }
    };

    for (const audio of activeCombatAudio) {
      fadeCancels.push(
        fadeAudioElement(audio, 0, DEFEAT_AUDIO_FADE_OUT_MS, () => {
          stopCombatAudio(audio);
        })
      );
    }

    const playDelay = activeCombatAudio.size > 0 ? DEFEAT_AUDIO_FADE_OUT_MS : 0;
    const playTimer = window.setTimeout(() => {
      let victoryAudio = victoryFanfareRef.current;
      if (!victoryAudio) {
        victoryAudio = new Audio(pickAudioFormatSrc(VICTORY_FANFARE_SRC_BASE));
        victoryAudio.preload = 'auto';
        victoryAudio.loop = false;
        victoryFanfareRef.current = victoryAudio;
      }

      try {
        victoryAudio.pause();
        victoryAudio.currentTime = 0;
      } catch {}

      victoryAudio.loop = false;
      victoryAudio.muted = false;
      victoryAudio.volume = 1;

      const playPromise = victoryAudio.play();
      if (playPromise && typeof (playPromise as Promise<void>).catch === 'function') {
        (playPromise as Promise<void>).catch(() => undefined);
      }
    }, playDelay);

    return () => {
      window.clearTimeout(playTimer);
      for (const cancel of fadeCancels) cancel();
    };
  }, [state.phase, state.rewardChoices.length, suppressStandardRewards]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    if (state.phase !== 'gameover') {
      const fanfareAudio = defeatFanfareRef.current;
      if (fanfareAudio) {
        fanfareAudio.pause();
        try {
          fanfareAudio.currentTime = 0;
        } catch {}
      }
      const burnSfxAudio = defeatBurnSfxRef.current;
      if (burnSfxAudio) {
        burnSfxAudio.pause();
        try {
          burnSfxAudio.currentTime = 0;
        } catch {}
      }
      return;
    }

    const activeCombatAudio = new Set<HTMLMediaElement>();
    const singletonCombatAudio = (window as any).__dd_combatMusic;
    if (singletonCombatAudio instanceof HTMLMediaElement) {
      activeCombatAudio.add(singletonCombatAudio);
    }
    if (bossMusicRef.current instanceof HTMLMediaElement) {
      activeCombatAudio.add(bossMusicRef.current);
    }

    const mediaEls = Array.from(document.querySelectorAll('audio')) as HTMLMediaElement[];
    for (const el of mediaEls) {
      const src = el.currentSrc || el.src || '';
      if (isDefaultCombatThemeSrc(src) || isMidBossMusicSrc(src)) {
        activeCombatAudio.add(el);
      }
    }

    const fadeCancels: Array<() => void> = [];
    const stopCombatAudio = (audio: HTMLMediaElement) => {
      try {
        audio.pause();
      } catch {}
      try {
        audio.currentTime = 0;
      } catch {}
      try {
        audio.volume = 0;
      } catch {}
      if ((window as any).__dd_combatMusic === audio) {
        try {
          audio.src = '';
        } catch {}
        (window as any).__dd_combatMusic = null;
      }
    };

    for (const audio of activeCombatAudio) {
      fadeCancels.push(
        fadeAudioElement(audio, 0, DEFEAT_AUDIO_FADE_OUT_MS, () => {
          stopCombatAudio(audio);
        })
      );
    }

    const playDelay = activeCombatAudio.size > 0 ? DEFEAT_AUDIO_FADE_OUT_MS : 0;
    const playTimer = window.setTimeout(() => {
      let fanfareAudio = defeatFanfareRef.current;
      if (!fanfareAudio) {
        fanfareAudio = new Audio(pickAudioFormatSrc(DEFEAT_FANFARE_SRC_BASE));
        fanfareAudio.preload = 'auto';
        fanfareAudio.loop = false;
        defeatFanfareRef.current = fanfareAudio;
      }

      try {
        fanfareAudio.pause();
        fanfareAudio.currentTime = 0;
      } catch {}

      fanfareAudio.loop = false;
      fanfareAudio.muted = false;
      fanfareAudio.volume = 1;

      const playPromise = fanfareAudio.play();
      if (playPromise && typeof (playPromise as Promise<void>).catch === 'function') {
        (playPromise as Promise<void>).catch(() => undefined);
      }
    }, playDelay);

    return () => {
      window.clearTimeout(playTimer);
      for (const cancel of fadeCancels) cancel();
    };
  }, [state.phase]);


  // Prevent stale defeat state from leaking into the next render.
  // We restart the video from frame 0 whenever the overlay changes phase,
  // then start playback after a short delay so the animation feels intentional.
  React.useLayoutEffect(() => {
    pendingDefeatVideoStartRef.current = false;
    setDefeatCardStage('idle');

    const video = defeatVideoRef.current;
    if (!video) return;

    try {
      video.pause();
      video.currentTime = 0;
    } catch {}
  }, [state.phase]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    if (state.phase !== 'gameover') {
      pendingDefeatVideoStartRef.current = false;
      setDefeatCardStage('idle');

      const video = defeatVideoRef.current;
      if (video) {
        try {
          video.pause();
          video.currentTime = 0;
        } catch {}
      }
      return;
    }

    pendingDefeatVideoStartRef.current = false;
    setDefeatCardStage('idle');
    setDefeatBurnPlaybackKey((key) => key + 1);

    const startBurnTimer = window.setTimeout(() => {
      pendingDefeatVideoStartRef.current = true;
      tryStartDefeatVideo();
    }, DEFEAT_BURN_START_DELAY_MS);

    return () => {
      pendingDefeatVideoStartRef.current = false;
      window.clearTimeout(startBurnTimer);
    };
  }, [state.phase, tryStartDefeatVideo]);

  // Fullscreen wrapper so the dungeon stays underneath (nice transition potential).
  return (
    <div
      className="fixed inset-0 z-[9999]"
      style={{ background: '#000' }}
    >
      {state.phase === 'loading' && (
        <div className="loading">
          <div className="loadingCard">
            <div className="title">Loading cards…</div>
            {state.error && <div className="error">{state.error}</div>}
          </div>
        </div>
      )}

      {state.phase === 'reward' && !suppressStandardRewards && (
        <Reward
          choices={state.rewardChoices}
          deck={state.pool}
          loot={lootFinalForReward}
          minDeckSize={REWARD_REMOVE_MIN_DECK_SIZE}
          onPickAdd={(cardId) => {
            const picked = library.find((c) => c.id === cardId) ?? null;
            exitToDungeonAfterReward(picked);
          }}
          onPickRemove={onlyRewardChoice ? undefined : (cardId) => {
            if (!canRemoveDeckCard(state.pool, cardId, REWARD_REMOVE_MIN_DECK_SIZE)) return;

            // Remove exactly that one card id from the deck we entered reward with.
            const nextIds = state.pool.map((c) => c.id).filter((id) => id !== cardId);

            // Exit reward and return the forced next deck ids.
            exitToDungeonAfterReward(null, nextIds);
          }}
          onEditDeck={() => {
            // Not exposed in dungeon mode.
            exitToDungeonAfterReward(null);
          }}
        />
      )}

      {(state.phase === 'player' || state.phase === 'enemy' || state.phase === 'setup' || state.phase === 'gameover') && (
        <>
          {state.phase !== 'gameover' && !shouldHoldCombatBoardForStarterCoin && (
         <Board
  state={state}
  relics={ownedRelics}
  maxHp={maxHp}
  maxSp={maxSp}
  torchProgress={torchProgress}
  lightRadius={lightRadius}
  enemyPortraitVariant={enemyPortraitVariant}
  lowFx={lowFx}
  aggressiveLowFx={aggressiveLowFx}
  suppressInitialTurnHandoff={suppressInitialTurnHandoff}
            onExitToDungeon={() => {
              const finalStats = { hp: state.player.hp, sp: state.player.sp, roomLightTier: String((state as any).lampKeeperRoomLight ?? '') || undefined };
              backToSetup();
              onDefeat(finalStats);
            }}
            onPlay={(cardId, addendumPay, xPaySp, addendumResource, xPayHp) =>
              dispatch(
                state.pendingDiscard
                  ? { type: 'DISCARD_FROM_HAND', cardId }
                  : {
                      type: 'PLAY_CARD',
                      cardId,
                      ...(addendumResource === 'hp' ? { addendumPayHp: addendumPay } : { addendumPaySp: addendumPay }),
                      xPaySp,
                      xPayHp,
                      ...(((state as any).pendingPrereqPlay?.cardId === cardId) ? { skipDiscardPrereq: true } : {}),
                    }
              )
            }
            onPrepareDiscardPrereqPlay={(cardId, addendumPay, xPaySp, addendumResource, xPayHp) =>
              dispatch({
                type: 'PREPARE_DISCARD_PREREQ_PLAY',
                cardId,
                ...(addendumResource === 'hp' ? { addendumPayHp: addendumPay } : { addendumPaySp: addendumPay }),
                xPaySp,
                xPayHp,
              } as any)
            }
            onAcknowledgeEnemy={() => dispatch({ type: 'ACK_ENEMY_CARD' })}
            onConfirmPeek={(orderedIds) => dispatch({ type: 'CONFIRM_PEEK_ORDER', orderedIds })}
            onConfirmChoice={(optionIndex) => dispatch({ type: 'CONFIRM_CHOICE', optionIndex })}
            onConfirmX={(value) => dispatch({ type: 'CONFIRM_X', value })}
            onConfirmTutor={(cardId) => dispatch({ type: 'CONFIRM_TUTOR_PICK', cardId })}
            onConfirmTopPick={(cardId) => dispatch({ type: 'CONFIRM_TOP_PICK', cardId })}
            onSkipTurn={() => dispatch({ type: 'SKIP_PLAYER_TURN' })}
            onReshuffleSelected={(cardIds) => dispatch({ type: 'RESHUFFLE_SELECTED', cardIds })}
            onDeckBuilder={() => {
              // Not exposed in dungeon mode.
            }}
            onReload={() => {
              // Don't reload the whole app; just re-run combat.
              startedRef.current = false;
              dispatch({ type: 'BACK_TO_SETUP' });
            }}
          />
          )}

          {state.phase === 'gameover' && (
            <div className="combatDefeatScreen" role="dialog" aria-modal="true" aria-labelledby="combat-defeat-title">
              <div className="combatDefeatScreen__content">
                <h1 id="combat-defeat-title" className="combatDefeatScreen__title">
                  Defeat!
                </h1>
                <p className="combatDefeatScreen__subtitle">
                  Your descent has reached its conclusion.
                </p>

                <div className="combatDefeatScreen__cardWrap">
                  <img
                    src={circleOneImg}
                    alt=""
                    aria-hidden="true"
                    className="combatDefeatScreen__sigil"
                    draggable={false}
                  />
                  <div className="combatDefeatScreen__cardStack">
                    <video
                      key={defeatBurnPlaybackKey}
                      ref={defeatVideoRef}
                      className="combatDefeatScreen__card"
                      src={fateBurnVideoSrc}
                      muted
                      playsInline
                      preload="metadata"
                      onLoadedData={() => {
                        const video = defeatVideoRef.current;
                        if (!video) return;

                        try {
                          video.pause();
                          if (defeatCardStage === 'idle') {
                            video.currentTime = 0;
                          }
                        } catch {}

                        if (pendingDefeatVideoStartRef.current && defeatCardStage !== 'done') {
                          window.requestAnimationFrame(() => {
                            tryStartDefeatVideo();
                          });
                        }
                      }}
                      onEnded={() => {
                        pendingDefeatVideoStartRef.current = false;
                        setDefeatCardStage('done');
                      }}
                    />
                    {defeatCardStage === 'burning' && (
                      <div
                        key={`burn-particles-${defeatBurnPlaybackKey}`}
                        className="combatDefeatScreen__particles"
                        aria-hidden="true"
                      >
                        {defeatBurnParticles.map((particle) => (
                          <span
                            key={particle.id}
                            className="combatDefeatScreen__particle"
                            style={particle.style}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="combatDefeatScreen__button"
                  onClick={() => {
                    const finalStats = { hp: state.player.hp, sp: state.player.sp, roomLightTier: String((state as any).lampKeeperRoomLight ?? '') || undefined };
                    backToSetup();
                    onDefeat(finalStats);
                  }}
                >
                  <img
                    src={buttonLoreImg}
                    alt=""
                    aria-hidden="true"
                    className="combatDefeatScreen__buttonBg"
                    draggable={false}
                  />
                  <span className="combatDefeatScreen__buttonLabel">Back to Main Menu</span>
                </button>
              </div>
            </div>
          )}
          {starterCoinState === 'playing' && (
            <div className="combatStarterCoinIntro" aria-hidden="true">
              <div
                key={`${starterCoinOutcome}-${starterCoinLaunchTokenRef.current}`}
                className={`combatStarterCoinActor combatStarterCoinActor--${starterCoinOutcome}`}
                style={{
                  '--combat-starter-coin-duration': `${starterCoinAnimationDurationMs}ms`,
                  '--combat-starter-coin-width': `min(${(32 * starterCoinConfig.sizeFactor).toFixed(2)}vw, ${(220 * starterCoinConfig.sizeFactor).toFixed(0)}px)`,
                  '--combat-starter-coin-width-mobile': `min(${(48 * starterCoinConfig.sizeFactor).toFixed(2)}vw, ${(184 * starterCoinConfig.sizeFactor).toFixed(0)}px)`,
                } as React.CSSProperties}
              >
                <div className="combatStarterCoinShadow" />
                <div className="combatStarterCoinStage">
                  <div className="combatStarterCoinSheetViewport" ref={starterCoinViewportRef}>
                    <div
                      className="combatStarterCoinSheet"
                      ref={starterCoinSheetRef}
                      aria-hidden="true"
                      style={{ backgroundImage: `url(${starterCoinConfig.sheetUrl})` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
